import { Head, router } from '@inertiajs/react'
import { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/components/ui/PageHeader'
import Notification from '@/components/ui/Notification'
import {
  Dropdown, DropdownButton, DropdownMenu, DropdownItem, DropdownLabel,
} from '@/components/catalyst/dropdown'
import {
  ShoppingCartIcon, ArrowUpTrayIcon, ArrowDownTrayIcon, ArrowLeftIcon,
} from '@heroicons/react/24/outline'
import { XMarkIcon as XClose } from '@heroicons/react/24/outline'
import clsx from 'clsx'

// ─── Page contract — direct port of legacy
//   app/javascript/app/pages/CustomerOrders/Show.jsx ("Clubs Order Prep")
//   onto the new stack, intentionally mirroring the visual language of
//   the CO Show Products / Scanning surface (see ProductsTab.tsx).
//
// Why we inline the table here instead of using DataTable:
//   The scanning UI uses a `table-fixed` layout with a two-row <thead>
//   (totals row + uppercase column-headers with per-column resize
//   handles, divide-x between cells) and inline EditableCell cells with
//   a dashed underline. DataTable's API doesn't expose any of those
//   knobs, so cloning the inline table here keeps the visual contract
//   1:1 with ProductsTab.

// ─── Column metadata + widths ────────────────────────────

const COL_WIDTHS_KEY = 'clubs-order-prep-col-widths'

// Default widths roughly match legacy `defaultColumnWidths.js` but
// trimmed to fit a single horizontal scrollbar instead of an antd grid.
const DEFAULT_COL_WIDTHS: Record<string, number> = {
  id: 60,
  upc: 130,
  clubs_order_prep_official_upc: 130,
  clubs_order_prep_sams_item_number: 130,
  style_no: 100,
  description: 180,
  clubs_order_prep_country_of_origin: 110,
  quantity: 75,
  selling_price: 85,
  in_stock: 90,
  clubs_order_prep_case_pack_qty: 95,
  total_cases: 90,
  clubs_order_prep_shippable_qty: 100,
  leftover: 90,
  clubs_order_prep_product_packaged_dimensions: 200,
  clubs_order_prep_product_packaged_weight: 130,
  clubs_order_prep_case_pack_dimensions: 200,
  clubs_order_prep_case_pack_weight: 120,
  clubs_order_prep_pallet_height: 100,
  clubs_order_prep_cases_per_pallet: 105,
  clubs_order_prep_pallet_weight: 110,
}
const MIN_COL_W = 30
const MAX_COL_W = 400

const COLUMNS: { key: keyof ClubsOrderPrepRow; label: string; numeric?: boolean; editable?: boolean }[] = [
  { key: 'id',                                           label: 'ID' },
  { key: 'upc',                                          label: 'Scanned UPC' },
  { key: 'clubs_order_prep_official_upc',                label: 'Official UPC',                              editable: true },
  { key: 'clubs_order_prep_sams_item_number',            label: "Sam's Item Number",                         editable: true },
  { key: 'style_no',                                     label: 'Vendor SKU' },
  { key: 'description',                                  label: 'Product Description' },
  { key: 'clubs_order_prep_country_of_origin',           label: 'Country of Origin',                         editable: true },
  { key: 'quantity',                                     label: 'Order QTY',     numeric: true },
  { key: 'selling_price',                                label: 'Selling Price', numeric: true },
  { key: 'in_stock',                                     label: 'Received QTY',  numeric: true },
  { key: 'clubs_order_prep_case_pack_qty',               label: 'Case Pack QTY', numeric: true },
  { key: 'total_cases',                                  label: 'Total Cases',   numeric: true },
  { key: 'clubs_order_prep_shippable_qty',               label: 'Shippable QTY', numeric: true, editable: true },
  { key: 'leftover',                                     label: 'Leftover QTY',  numeric: true },
  { key: 'clubs_order_prep_product_packaged_dimensions', label: 'Pkg Dimensions (L×W×H), in',                editable: true },
  { key: 'clubs_order_prep_product_packaged_weight',     label: 'Pkg Weight, lbs',     numeric: true,         editable: true },
  { key: 'clubs_order_prep_case_pack_dimensions',        label: 'Case Pack Dimensions (L×W×H), in',          editable: true },
  { key: 'clubs_order_prep_case_pack_weight',            label: 'Case Pack Weight, lbs', numeric: true,       editable: true },
  { key: 'clubs_order_prep_pallet_height',               label: 'Pallet Height, in',     numeric: true,       editable: true },
  { key: 'clubs_order_prep_cases_per_pallet',            label: 'Cases Per Pallet',      numeric: true,       editable: true },
  { key: 'clubs_order_prep_pallet_weight',               label: 'Pallet Weight, lbs',    numeric: true,       editable: true },
]

// Numeric columns whose summary row sums.
const SUMMED_COLS: (keyof ClubsOrderPrepRow)[] = [
  'quantity', 'selling_price', 'in_stock',
  'clubs_order_prep_case_pack_qty', 'total_cases',
  'clubs_order_prep_shippable_qty', 'leftover',
  'clubs_order_prep_product_packaged_weight',
  'clubs_order_prep_case_pack_weight',
  'clubs_order_prep_pallet_weight',
]

// ─── Types ────────────────────────────────────────────────

export interface ClubsOrderPrepRow {
  id: number
  customer_order_id: number
  upc: string | null
  clubs_order_prep_official_upc: string | null
  clubs_order_prep_sams_item_number: string | null
  style_no: string | null
  description: string | null
  clubs_order_prep_country_of_origin: string | null
  quantity: number | null
  selling_price: number | null
  in_stock: number | null
  clubs_order_prep_case_pack_qty: number | null
  total_cases: number | null
  clubs_order_prep_shippable_qty: number | null
  leftover: number | false | null
  clubs_order_prep_product_packaged_dimensions: string | null
  clubs_order_prep_product_packaged_weight: number | null
  clubs_order_prep_case_pack_dimensions: string | null
  clubs_order_prep_case_pack_weight: number | null
  clubs_order_prep_pallet_height: number | null
  clubs_order_prep_cases_per_pallet: number | null
  clubs_order_prep_pallet_weight: number | null
}

interface ClubsOrderPrepProps {
  customer_order_id: number
  clubs_order_prep_table_data: ClubsOrderPrepRow[]
  case_labels: string[]
}

// ─── Column width hook (parallel to useProductColumnWidths) ──

function useClubColumnWidths() {
  const [widths, setWidths] = useState<Record<string, number>>(() => {
    if (typeof window === 'undefined') return {}
    try { return JSON.parse(localStorage.getItem(COL_WIDTHS_KEY) || '{}') } catch { return {} }
  })
  useEffect(() => { localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(widths)) }, [widths])

  const getW = (col: string) => widths[col] || DEFAULT_COL_WIDTHS[col] || 100

  const [resizingCol, setResizingCol] = useState<string | null>(null)
  const startX = useRef(0)
  const startW = useRef(0)

  const startResize = useCallback((col: string, e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation()
    setResizingCol(col)
    startX.current = e.clientX
    startW.current = widths[col] || DEFAULT_COL_WIDTHS[col] || 100
  }, [widths])

  useEffect(() => {
    if (!resizingCol) return
    const onMove = (e: MouseEvent) => {
      const nw = Math.max(MIN_COL_W, Math.min(MAX_COL_W, startW.current + e.clientX - startX.current))
      setWidths(prev => ({ ...prev, [resizingCol]: nw }))
    }
    const onUp = () => setResizingCol(null)
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [resizingCol])

  return { getW, startResize, isResizing: !!resizingCol }
}

// ─── EditableCell (same UX as ProductsTab: dashed underline → click →
//                   inline input → Enter / blur commits, Esc cancels) ──

function EditableCell({
  value, onSave, type = 'text', emptyAsZero = false,
}: {
  value: string | number | null
  onSave: (val: string) => void
  type?: string
  /** When true, an empty value is shown as "0" rather than "Empty". */
  emptyAsZero?: boolean
}) {
  const original = String(value ?? '')
  const [val, setVal] = useState(original)
  const [editing, setEditing] = useState(false)
  const cellRef = useRef<HTMLSpanElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [width, setWidth] = useState<number | undefined>()

  useEffect(() => { if (!editing) setVal(String(value ?? '')) }, [value, editing])

  const commit = () => {
    setEditing(false)
    if (val !== original) onSave(val)
  }
  const cancel = () => { setVal(original); setEditing(false) }

  const startEdit = () => {
    const captured = cellRef.current?.offsetWidth ?? 0
    setWidth(Math.max(captured, 64))
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const displayEmpty = emptyAsZero ? '0' : 'Empty'

  return (
    <div className="flex h-6 items-center">
      {editing ? (
        <div className="relative inline-flex items-center" style={{ width: width ? `${width + 20}px` : undefined }}>
          <input
            ref={inputRef}
            type={type}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel() }}
            className="min-w-16 w-full rounded border border-indigo-300 bg-white py-0.5 pl-1 pr-5 text-xs focus:outline-none dark:bg-zinc-800 dark:border-indigo-500/50"
            autoFocus
          />
          {val && (
            <button type="button" onMouseDown={(e) => { e.preventDefault(); setVal('') }}
              className="absolute right-1 text-zinc-300 hover:text-zinc-500 dark:text-zinc-600">
              <XClose className="h-3 w-3" />
            </button>
          )}
        </div>
      ) : (
        <span
          ref={cellRef}
          className={clsx(
            'cursor-pointer border border-transparent border-b-zinc-300 border-dashed px-1 py-0.5 dark:border-b-zinc-600',
            val === '' && !emptyAsZero && 'text-rose-500',
          )}
          onClick={startEdit}
        >
          {val !== '' ? val : displayEmpty}
        </span>
      )}
    </div>
  )
}

// Official UPC: red highlight when present-but-differs-from-scanned.
function OfficialUpcCell({ row, onSave }: { row: ClubsOrderPrepRow; onSave: (v: string) => void }) {
  const mismatch = !!row.clubs_order_prep_official_upc && row.clubs_order_prep_official_upc !== row.upc
  return (
    <span className={mismatch ? 'inline-block rounded bg-rose-600 px-1 text-white' : ''}>
      <EditableCell value={row.clubs_order_prep_official_upc} onSave={onSave} />
    </span>
  )
}

// ─── Page ──────────────────────────────────────────────────

export default function ClubsOrderPrep({ customer_order_id, clubs_order_prep_table_data, case_labels }: ClubsOrderPrepProps) {
  const [rows, setRows] = useState(clubs_order_prep_table_data)
  useEffect(() => { setRows(clubs_order_prep_table_data) }, [clubs_order_prep_table_data])

  const [notif, setNotif] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const uploadRef = useRef<HTMLInputElement>(null)

  const csrfToken = typeof document !== 'undefined'
    ? (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ''
    : ''

  const showNotif = (msg: string) => { setNotif(msg); setTimeout(() => setNotif(null), 4000) }

  // ─── Sort (single-column, click header to toggle) ──
  const [sortCol,     setSortCol]     = useState<keyof ClubsOrderPrepRow | null>(null)
  const [sortDir,     setSortDir]     = useState<'asc' | 'desc'>('asc')
  const toggleSort = (col: keyof ClubsOrderPrepRow) => {
    if (sortCol === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  const sortedRows = useMemo(() => {
    if (!sortCol) return rows
    const dir = sortDir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const va = a[sortCol], vb = b[sortCol]
      if (va == null && vb == null) return 0
      if (va == null) return  1
      if (vb == null) return -1
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
      return String(va).localeCompare(String(vb)) * dir
    })
  }, [rows, sortCol, sortDir])

  // ─── Totals row aggregation (sum of numeric columns) ──
  const totals = useMemo(() => {
    const acc = {} as Record<string, number>
    SUMMED_COLS.forEach(k => { acc[k as string] = 0 })
    rows.forEach(r => {
      SUMMED_COLS.forEach(k => {
        const v = r[k]
        if (typeof v === 'number') acc[k as string] += v
      })
    })
    return acc
  }, [rows])

  // ─── Column widths + resize ──
  const { getW, startResize, isResizing } = useClubColumnWidths()

  // ─── Save field — POST to product update; optimistic local merge ──
  const saveField = (productId: number, field: keyof ClubsOrderPrepRow, value: string) => {
    setRows(prev => prev.map(r => r.id === productId ? { ...r, [field]: value } as ClubsOrderPrepRow : r))
    router.post(
      `/inertia_customer_orders/${customer_order_id}/product/${productId}`,
      { product: { [field as string]: value } },
      {
        headers: { 'X-CSRF-Token': csrfToken },
        preserveScroll: true,
        onSuccess: () => showNotif('Product successfully updated.'),
        onError:   () => showNotif('Failed to update product.'),
      },
    )
  }

  // ─── Update From Excel — multipart POST ──
  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const form = new FormData()
      form.append('products_file', file)
      const res = await fetch(
        `/customer_orders/${customer_order_id}/update_clubs_order_prep_products_from_excel`,
        { method: 'POST', body: form, headers: { 'X-CSRF-Token': csrfToken, Accept: 'application/json' } },
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({ message: `HTTP ${res.status}` }))
        throw new Error(body.message || `HTTP ${res.status}`)
      }
      showNotif('Products uploaded successfully.')
      router.reload({ preserveScroll: true })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed'
      showNotif(`Products updating failed: ${msg}`)
    } finally {
      setUploading(false)
      if (uploadRef.current) uploadRef.current.value = ''
    }
  }

  // Numeric value formatter — matches legacy RoundToTwoDecimalsTypeProvider
  // (selling_price + the two weight columns shown with 2 decimal places).
  const TWO_DECIMAL_COLS = new Set<string>([
    'selling_price',
    'clubs_order_prep_product_packaged_weight',
    'clubs_order_prep_pallet_weight',
  ])
  const fmtTotal = (key: string, value: number) =>
    TWO_DECIMAL_COLS.has(key) ? value.toFixed(2) : String(value)

  // Header cell with sort + resize handle (same pattern as ProductsTab
  // `sortTh`). Uppercase, [10px], tracking-wider.
  const renderHeaderCell = (col: typeof COLUMNS[number]) => (
    <th
      key={col.key as string}
      data-testid={`cop-th-${col.key as string}`}
      // `top-[28px]` parks this row right under the totals row (which is
      // ~28px tall: `py-1.5` + `text-[10px]` line-height). border-r picks
      // up the divider since divide-x stops working on sticky cells.
      className="sticky top-[28px] z-10 relative overflow-hidden border-r border-b border-zinc-200 bg-white py-2 pl-2 pr-4 select-none dark:border-zinc-700 dark:bg-zinc-900"
      style={{ width: getW(col.key as string) }}
    >
      <span className="flex min-w-0 max-w-full items-center gap-0.5">
        <span
          className="min-w-0 cursor-pointer truncate"
          onClick={() => toggleSort(col.key)}
        >
          {col.label}
          {sortCol === col.key && (
            <span className="ml-0.5 text-indigo-500">{sortDir === 'asc' ? '↑' : '↓'}</span>
          )}
        </span>
      </span>
      <div
        onMouseDown={(e) => startResize(col.key as string, e)}
        className="absolute inset-y-0 right-0 w-1 cursor-col-resize hover:bg-indigo-500/30 active:bg-indigo-500/50"
      />
    </th>
  )

  // Render a single body cell — read-only / editable / Official-UPC variants.
  const renderBodyCell = (row: ClubsOrderPrepRow, col: typeof COLUMNS[number]) => {
    const key = col.key as string
    const rawVal = row[col.key]

    if (col.key === 'clubs_order_prep_official_upc') {
      return (
        <td key={key} data-testid={`cop-cell-${row.id}-${key}`} className="py-1 pl-2 pr-2">
          <OfficialUpcCell row={row} onSave={(v) => saveField(row.id, col.key, v)} />
        </td>
      )
    }

    if (col.editable) {
      const emptyAsZero = col.numeric
      return (
        <td key={key} data-testid={`cop-cell-${row.id}-${key}`} className="py-1 pl-2 pr-2">
          <EditableCell
            value={rawVal as string | number | null}
            onSave={(v) => saveField(row.id, col.key, v)}
            type={col.numeric ? 'number' : 'text'}
            emptyAsZero={emptyAsZero}
          />
        </td>
      )
    }

    // Read-only cell
    const displayVal = (() => {
      if (col.key === 'selling_price') return Number(rawVal ?? 0).toFixed(2)
      if (col.key === 'leftover')      return rawVal === false ? 0 : (rawVal ?? 0)
      if (col.numeric)                 return rawVal ?? 0
      return rawVal ?? '—'
    })()

    return (
      <td key={key} data-testid={`cop-cell-${row.id}-${key}`} className="py-1 pl-2 pr-2 text-zinc-700 dark:text-zinc-300">
        {displayVal as React.ReactNode}
      </td>
    )
  }

  return (
    <>
      <Head title={`Clubs Order Prep #${customer_order_id}`} />
      <Notification message={notif ?? ''} show={!!notif} onClose={() => setNotif(null)} />

      <PageHeader
        title="Clubs Order Prep"
        subtitle={`Customer Order #${customer_order_id}`}
        icon={<ShoppingCartIcon className="h-5 w-5" />}
        actions={
          <div className="flex items-center gap-2" data-testid="cop-toolbar">
            <a
              href={`/inertia_customer_orders/${customer_order_id}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
              data-testid="cop-back"
            >
              <ArrowLeftIcon className="h-4 w-4" /> Back
            </a>

            <Dropdown>
              <DropdownButton
                as="button"
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700"
                data-testid="cop-export-trigger"
              >
                <ArrowDownTrayIcon className="h-4 w-4" /> Export Case Labels (pdf)
              </DropdownButton>
              <DropdownMenu anchor="bottom end" className="z-50 min-w-72">
                {case_labels.map((label) => (
                  <DropdownItem
                    key={label}
                    href={`/customer_order/${customer_order_id}/export_case_labels.pdf?category=${encodeURIComponent(label)}`}
                    target="_blank"
                    rel="noopener"
                    data-testid={`cop-export-${label}`}
                  >
                    <DropdownLabel>{label}</DropdownLabel>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>

            <button
              type="button"
              onClick={() => uploadRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
              data-testid="cop-update-from-excel"
            >
              <ArrowUpTrayIcon className="h-4 w-4" /> {uploading ? 'Uploading…' : 'Update From Excel'}
            </button>
            <input
              ref={uploadRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              className="hidden"
              onChange={onFileChosen}
              data-testid="cop-file-input"
            />
          </div>
        }
      />

      {/* ── Table ──
          The wrapper has BOTH overflow-x and overflow-y so the
          horizontal scrollbar parks at the bottom of THIS container,
          not at the bottom of the whole page. Without the height cap
          a tall table would push the scrollbar off-screen and force
          the user to scroll the page to reach it — exactly the bug
          we're fixing here. Legacy used `height={window.innerHeight -
          210}` for the same reason.

          The two header rows are made sticky via top-0 / top-[28px]
          so they survive vertical scrolling within the wrapper. */}
      <div
        data-testid="clubs-order-prep"
        className="overflow-auto rounded-lg border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900"
        style={{ maxHeight: 'calc(100vh - 200px)', cursor: isResizing ? 'col-resize' : undefined }}
      >
        {rows.length === 0 ? (
          <p className="py-6 text-center text-xs text-zinc-400">No products to prep yet.</p>
        ) : (
          <table className="w-full table-fixed text-left text-xs text-zinc-900 dark:text-zinc-100 [&>tbody>tr>td]:truncate">
            <colgroup>
              {COLUMNS.map(c => (
                <col key={c.key as string} style={{ width: getW(c.key as string) }} />
              ))}
            </colgroup>
            <thead>
              {/* ── Totals row ── (same band as ProductsTab).
                  Per-cell `sticky top-0` because <thead>/<tr> can't be
                  position:sticky reliably across browsers — `<th>` can. */}
              <tr
                className="text-[10px] font-semibold text-zinc-600 dark:text-zinc-400"
                data-testid="cop-summary-row"
              >
                {COLUMNS.map((col) => {
                  const k = col.key as string
                  const isSum = (SUMMED_COLS as string[]).includes(k)
                  return (
                    <th
                      key={k}
                      className="sticky top-0 z-20 border-b border-zinc-200 bg-zinc-50 py-1.5 pr-2 dark:border-zinc-700 dark:bg-zinc-800"
                    >
                      {isSum ? fmtTotal(k, totals[k] ?? 0) : ''}
                    </th>
                  )
                })}
              </tr>
              {/* ── Column headers (uppercase, divide-x) ── */}
              <tr className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                {COLUMNS.map(renderHeaderCell)}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {sortedRows.map((row) => (
                <tr key={row.id} className="group hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
                  {COLUMNS.map(col => renderBodyCell(row, col))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

ClubsOrderPrep.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
